import React, { useState } from 'react'
import styles from './FromFile.module.scss'
import { UploadCloud as UploadIcon } from 'almanac-ui/icons/16/UploadCloud'
import { ReactComponent as FileIcon } from 'assets/icons/doc.svg'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { SourceCard } from 'components/SourceCard/SourceCard'
import { Text } from 'blaze-ui/Text/Text'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

export type FromFileProps = {
  onChange: (sampleText: string, filename: string) => void
}

const validFileExtensions = ['md', 'txt', 'pdf']

const isValidFile = (maybeValidFile: DataTransferItem) => {
  const isFile = maybeValidFile.kind === 'file'
  if (!isFile) return false
  const file = maybeValidFile.getAsFile()
  if (!file) return false
  const isValidExtension = validFileExtensions.some((ext) => file.name.endsWith(ext))
  if (!isValidExtension) return false
  const hasFileSize = file.size > 0
  return hasFileSize
}

const processFile = async (file: File): Promise<string> => {
  const fileExtension = /\.(?<ext>\w+)$/g.exec(file.name)?.groups?.ext ?? ''
  switch (fileExtension) {
    case 'md':
    case 'txt':
      return await file.text()
    case 'pdf': {
      const formData = new FormData()
      formData.append('pdf_file', file)
      const response = await sendToApi<{ text: string }>('/extract-pdf-text', {
        method: 'POST',
        body: formData,
      })
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(`Failed to extract PDF text: ${response}`)
      }
      return response.data.text
    }
    default:
      throw new Error(`Unsupported file extension: ${fileExtension}`)
  }
}

export const FromFile: React.FC<React.PropsWithChildren<FromFileProps>> = ({ onChange }) => {
  const [fileName, setFileName] = useState('')
  const [uploaded, setUploaded] = useState(false)

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setUploaded(false)
    const items = Array.from(e.dataTransfer.items)
    if (items && items.length === 1 && isValidFile(items[0])) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      uploadFile(items[0].getAsFile()!)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const handleFilePickerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files[0].size > 10 * 1024 * 1024) {
      showToastMessage('The file is bigger than the 10MB limit, please use a smaller file', 'error')
    } else {
      if (files && files.length === 1 && files[0].size > 0) {
        setUploaded(false)
        uploadFile(files[0])
      }
    }
  }

  const uploadFile = async (file: File) => {
    try {
      setFileName(file.name)
      const fileContents = await processFile(file)
      setUploaded(true)
      onChange(fileContents, file.name)
    } catch (e) {
      setFileName('')
      setUploaded(false)
    }
  }

  return (
    <div className={styles.FromFile}>
      {!fileName && (
        <div>
          <div className={styles.dropZone} onDrop={handleDrop} onDragOver={handleDragOver}>
            <div className={styles.instruction}>Drag & drop file</div>
            <div className={styles.fileTypePills}>
              <div className={styles.pill}>Markdown</div>
              <div className={styles.pill}>TXT</div>
              <div className={styles.pill}>PDF</div>
            </div>
            <div className={styles.filePickerBtn}>
              <input
                type="file"
                className={styles.filePickerInput}
                accept={validFileExtensions.map((x) => `.${x}`).join(',')}
                onChange={handleFilePickerChange}
                data-testid="brand-file-input"
              />
              <UploadIcon />
              Upload from Your Device
            </div>
          </div>
          <div className={styles.maxFileSize}>
            <Text variant="label">Max file size: 10MB</Text>
          </div>
        </div>
      )}
      {fileName && (
        <SourceCard
          icon={<FileIcon className={styles.fileIcon} />}
          title={`${fileName}${uploaded ? '' : '(Uploading...)'}`}
          onDismiss={() => {
            setFileName('')
            setUploaded(false)
          }}
        />
      )}
    </div>
  )
}
